import React, { useId, useMemo } from 'react';
import { DEFAULT_TEXT_PROPS } from '../../context/EditorContext';
import { layoutWordArt, getTransform, fontFeatureSettings } from '../../lib/wordart';
import { textEffectShadows, textReflectionCss } from '../../lib/effects';
import { useFontsVersion } from '../../hooks/useFontsVersion';
import { changeCase } from '../../lib/textUtils';
import { GradientSvgDefs } from '../../lib/gradients';

const NOMINAL = 100;

const applyTextTransform = (text, tt) => {
  if (!tt || tt === 'none') return text;
  if (tt === 'uppercase') return text.toUpperCase();
  if (tt === 'lowercase') return text.toLowerCase();
  if (tt === 'capitalize') return changeCase ? changeCase(text, 'capitalize') : text;
  return text;
};

/** SVG paint (fill) for a text fill, as defs + paint url. `k` converts page px to viewBox user units. */
const buildPaint = (fill, color, uid, vb, k) => {
  const [vx, vy, vw, vh] = vb;
  if (!fill || fill.type === 'none') return { defs: null, paint: 'none' };
  if (fill.type === 'solid' || !fill.type) return { defs: null, paint: fill.color || color };
  const id = `${uid}-paint`;
  if (fill.type === 'gradient' && fill.gradient) {
    // Geometry in page pixels (the text's box is vw/k.x × vh/k.y px), mapped into the viewBox — same picture as CSS.
    const kx = k?.x || 1, ky = k?.y || 1;
    return { paint: `url(#${id})`, defs: <GradientSvgDefs id={id} gradient={fill.gradient} width={vw / kx} height={vh / ky} tx={vx} ty={vy} sx={kx} sy={ky} /> };
  }
  if (fill.type === 'image' && fill.image?.url) {
    if (fill.image.fit === 'tile') {
      const tw = 160 * k.x, th = 160 * k.y;
      return { paint: `url(#${id})`, defs: <pattern id={id} patternUnits="userSpaceOnUse" width={tw} height={th}><image href={fill.image.url} width={tw} height={th} preserveAspectRatio="none" /></pattern> };
    }
    return { paint: `url(#${id})`, defs: <pattern id={id} patternUnits="userSpaceOnUse" x={vx} y={vy} width={vw} height={vh}><image href={fill.image.url} x="0" y="0" width={vw} height={vh} preserveAspectRatio={fill.image.fit === 'contain' ? 'xMidYMid meet' : fill.image.fit === 'stretch' ? 'none' : 'xMidYMid slice'} /></pattern> };
  }
  if (fill.type === 'pattern') {
    const fg = fill.color || '#000000', bg = fill.background || '#ffffff';
    const cell = 8;
    const w = cell * k.x, h = cell * k.y;
    let art;
    switch (fill.pattern) {
      case 'dots': art = <circle cx={w / 2} cy={h / 2} r={Math.min(w, h) * 0.2} fill={fg} />; break;
      case 'hstripes': art = <rect x="0" y="0" width={w} height={h * 0.35} fill={fg} />; break;
      case 'vstripes': art = <rect x="0" y="0" width={w * 0.35} height={h} fill={fg} />; break;
      case 'grid': art = <><rect x="0" y="0" width={w} height={h * 0.15} fill={fg} /><rect x="0" y="0" width={w * 0.15} height={h} fill={fg} /></>; break;
      case 'checker': art = <><rect x="0" y="0" width={w / 2} height={h / 2} fill={fg} /><rect x={w / 2} y={h / 2} width={w / 2} height={h / 2} fill={fg} /></>; break;
      case 'diamonds': art = <polygon points={`${w / 2},0 ${w},${h / 2} ${w / 2},${h} 0,${h / 2}`} fill={fg} />; break;
      default: art = <line x1="0" y1={h} x2={w} y2="0" stroke={fg} strokeWidth={Math.min(w, h) * 0.3} />; // stripes (dark upward diagonal)
    }
    return { paint: `url(#${id})`, defs: <pattern id={id} patternUnits="userSpaceOnUse" width={w} height={h}><rect width={w} height={h} fill={bg} />{art}</pattern> };
  }
  return { defs: null, paint: color };
};

/**
 * Renders a text box whose text follows a WordArt transform (arch, wave, triangle…) as SVG glyphs that
 * stretch to fill the frame, like legacy Publisher WordArt. Fill/outline/effects come from the WordArt style.
 */
export const WordArtSvg = ({ object, scale = 1, text, contentW, contentH, style = {} }) => {
  const p = { ...DEFAULT_TEXT_PROPS, ...(object.properties || {}) };
  const uid = useId().replace(/:/g, '');
  const fontsVersion = useFontsVersion();
  const transform = getTransform(p.wordArt?.transform);
  const shown = applyTextTransform(String(text ?? ''), p.textTransform);
  const layout = useMemo(
    () => layoutWordArt(shown, p, transform.id),
    // `p` is rebuilt every render; the glyph layout (text measurement) depends on exactly these of its fields — colour,
    // fill and outline changes must not re-measure the text
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shown, p.fontFamily, p.fontWeight, p.fontStyle, p.fontVariant, p.textAlign, transform.id, fontsVersion],
  );
  const [vx, vy, vw, vh] = layout.viewBox;
  const W = Math.max(1, contentW || object.width), H = Math.max(1, contentH || object.height);
  const k = { x: vw / W, y: vh / H }; // page px -> user units per axis
  const color = p.textFill?.type === 'solid' && p.textFill.color ? p.textFill.color : p.color || '#000000';
  const { defs, paint } = buildPaint(p.textFill, color, uid, layout.viewBox, k);
  const outlineW = p.textOutline?.width ? p.textOutline.width * ((k.x + k.y) / 2) : 0;
  const shadows = textEffectShadows(p.textEffects || {}, scale);
  const fontWeight = p.fontWeight === 'bold' || p.fontWeight === '700' ? 700 : p.fontWeight || 400;

  const svgStyle = {
    display: 'block', width: '100%', height: '100%', overflow: 'visible',
    filter: shadows.length ? shadows.map((s) => `drop-shadow(${s})`).join(' ') : undefined,
    WebkitBoxReflect: textReflectionCss(p.textEffects?.reflection, scale),
    transform: layout.css3d || undefined,
    transformOrigin: 'center',
    ...style,
  };

  if (!layout.glyphs.length) {
    return <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={svgStyle} data-testid="wordart-svg" data-transform={transform.id} aria-label="WordArt" />;
  }

  const fontAttrs = {
    fontFamily: `'${p.fontFamily || 'Inter'}', sans-serif`,
    fontSize: NOMINAL,
    fontWeight,
    fontStyle: p.fontStyle || 'normal',
    textAnchor: 'middle',
    style: { fontVariant: p.fontVariant === 'small-caps' ? 'small-caps' : 'normal', fontFeatureSettings: fontFeatureSettings(p), userSelect: 'none' },
  };
  const glyphs = layout.glyphs.map((g, i) => (
    <text key={`${i}-${g.ch}`} transform={`translate(${g.x.toFixed(2)} ${g.y.toFixed(2)})${g.rotate ? ` rotate(${g.rotate.toFixed(2)})` : ''}${g.sy !== 1 ? ` scale(1 ${g.sy.toFixed(4)})` : ''}`} xmlSpace="preserve">{g.ch}</text>
  ));
  const stroke = outlineW ? p.textOutline.color || '#000000' : 'none';
  // Gradient / picture / pattern paints are defined in viewBox space, but a paint server referenced by a *transformed*
  // glyph is resolved in that glyph's own (translated / rotated) space — every letter would sample the same spot.
  // So the glyphs become a clip path and one untransformed rectangle carries the paint: one gradient across the word,
  // exactly like the CSS rendering of a plain text box.
  const painted = defs && paint.startsWith('url(');
  const clipId = `${uid}-glyphs`;

  return (
    <svg viewBox={`${vx} ${vy} ${vw} ${vh}`} preserveAspectRatio="none" style={svgStyle} data-testid="wordart-svg" data-transform={transform.id} data-paint={painted ? 'clip' : 'direct'} role="img" aria-label={shown}>
      {defs && <defs>{defs}{painted && <clipPath id={clipId} {...fontAttrs}>{glyphs}</clipPath>}</defs>}
      {painted ? (
        <>
          {outlineW > 0 && <g fill="none" stroke={stroke} strokeWidth={outlineW} strokeLinejoin="round" {...fontAttrs}>{glyphs}</g>}
          <rect x={vx} y={vy} width={vw} height={vh} fill={paint} clipPath={`url(#${clipId})`} />
        </>
      ) : (
        <g fill={paint} stroke={stroke} strokeWidth={outlineW || undefined} strokeLinejoin="round" paintOrder="stroke fill markers" {...fontAttrs}>
          {glyphs}
        </g>
      )}
    </svg>
  );
};

/** Small transform preview used by galleries (renders a short sample with the given transform). */
export const WordArtTransformSample = ({ transform, size = 40, color = 'currentColor', text }) => {
  const sample = text || (transform === 'circle' ? 'Abc Abc Abc ' : 'Abc');
  const obj = useMemo(() => ({ id: 's', type: 'textbox', width: size, height: size * 0.6, properties: { ...DEFAULT_TEXT_PROPS, fontFamily: 'Outfit', fontWeight: 'bold', color, textFill: { type: 'solid', color }, wordArt: { transform } } }), [transform, size, color]);
  return <div style={{ width: size, height: size * 0.6 }}><WordArtSvg object={obj} text={sample} contentW={size} contentH={size * 0.6} /></div>;
};
